import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import { Formik, Form, FieldArray, useFormikContext } from 'formik';
import { Input } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { InputFormFiled } from '../../../components/Common/FormikField/InputFormFiled';
import { GetSchema, string_required } from '../../../js/Base/ValidationSchema';
import toastAlert from '../../../js/Base/ToastAlert';
import CloseIcon from '@mui/icons-material/Close';
import { createConfigKeyApi } from '../../../api/keyApi';
import {
  AWS_KEY_NAME,
  GCP_KEY_NAME,
  buildDefaultContents,
  getConfigKeyFields,
  mapGcpJsonToArray,
  normalizeKeyName,
} from './configKeyFields';

/** 與 Config Key 編輯頁 KeyContents 表格「刪除」相同：文字連結式按鈕 */
const KEY_CONTENTS_ROW_BTN_STYLE = {
  color: '#323232',
  fontSize: '13px',
  fontWeight: 'bold',
  padding: '0px',
};

/** 從表單的 key/value 陣列取出指定欄位值 */
const readFieldValue = (keyContentsArray, fieldKey) => {
  const pair = (keyContentsArray || []).find((item) => item?.key === fieldKey);
  return String(pair?.value || '').trim();
};

/**
 * key_name 變動時自動鋪好該雲別的固定欄位。
 * 以 ref 記住前一次的值，只在真的切換雲別時覆寫，避免 re-render 洗掉已輸入的內容。
 */
const ConfigKeyFieldsSync = () => {
  const { values, setFieldValue } = useFormikContext();
  const normalizedKeyName = normalizeKeyName(values.key_name);
  const previousKeyNameRef = useRef(normalizedKeyName);

  useEffect(() => {
    if (previousKeyNameRef.current === normalizedKeyName) return;
    previousKeyNameRef.current = normalizedKeyName;
    setFieldValue('key_contents_json_array', buildDefaultContents(normalizedKeyName));
  }, [normalizedKeyName, setFieldValue]);

  return null;
};

const CloudConfigKeyAddModal = ({ isModalOpen, handleClose, submitEffect, isCreate, configKey, source, existingConfigKeys = [] }) => {
  const projectId = localStorage.getItem('project_id');
  const existingGcpProjectIds = (existingConfigKeys || [])
    .filter((item) => normalizeKeyName(item?.key_name) === GCP_KEY_NAME)
    .map((item) => String(item?.gcp_project_id || item?.key_contents_json?.PROJECT_ID || '').trim())
    .filter(Boolean);
  const existingAwsAccountIds = (existingConfigKeys || [])
    .filter((item) => normalizeKeyName(item?.key_name) === AWS_KEY_NAME)
    .map((item) => String(item?.aws_account_id || '').trim())
    .filter(Boolean);
  const [gcpJsonText, setGcpJsonText] = useState('');
  const projectUuid = localStorage.getItem("project_uuid");
  const [initialValues, setInitialValues] = useState({
    key_name: '',
    project_no: projectId,
    project_uuid: projectUuid,
    remarks: '',
    expiry_date: new Date(),
    key_contents_json_array: [{ key: '', value: '' }],
  });

  useEffect(() => {
    if (!isModalOpen) {
      setInitialValues({
        key_name: '',
        project_no: projectId,
        project_uuid: projectUuid,
        remarks: '',
        expiry_date: new Date(),
        key_contents_json_array: [{ key: '', value: '' }],
      });
      setGcpJsonText('');
    }
  }, [isModalOpen]);

  const formSchema = GetSchema({
    key_name: string_required(),
    project_no: string_required(),
  });

  const resetForm = () => {
    setInitialValues({
      key_name: '',
      project_no: projectId,
      project_uuid: projectUuid,
      remarks: '',
      expiry_date: new Date(),
      key_contents_json_array: [{ key: '', value: '' }],
    });
    setGcpJsonText('');
  };

  const handleSubmit = async (values) => {
    const normalizedKeyName = normalizeKeyName(values.key_name);

    // GCP / AWS 可有多把 key（分別以 GCP 專案、AWS 帳戶區分），其餘雲端仍維持一專案一筆
    if (normalizedKeyName === 'AZURE') {
      const hasSameKeyInProject = (existingConfigKeys || []).some(
        (item) => normalizeKeyName(item?.key_name) === normalizedKeyName
      );
      if (hasSameKeyInProject) {
        toastAlert.autoClose(true).showError(`同一個專案僅能建立一筆 ${normalizedKeyName} Config Key`);
        return;
      }
    }

    if (normalizedKeyName === GCP_KEY_NAME) {
      const gcpProjectId = readFieldValue(values.key_contents_json_array, 'PROJECT_ID');
      if (!gcpProjectId) {
        toastAlert.autoClose(true).showError('請先填入 GCP Service Account 的 PROJECT_ID');
        return;
      }
      if (existingGcpProjectIds.includes(gcpProjectId)) {
        toastAlert.autoClose(true).showError(`此專案已存在 GCP 專案 ${gcpProjectId} 的 Service Account Key`);
        return;
      }
    }

    if (normalizedKeyName === AWS_KEY_NAME) {
      // AWS 帳戶 ID 要打 STS 才知道，前端只能擋掉空欄位，重複由後端判斷
      const missing = ['REGION', 'SECRET_ARN'].filter(
        (field) => !readFieldValue(values.key_contents_json_array, field)
      );
      if (missing.length > 0) {
        toastAlert.autoClose(true).showError(`請先填入 AWS ${missing.join('、')}`);
        return;
      }
    }

    if (isCreate) {
      await createConfigKey(values);
    }
  };

  const createConfigKey = async (values) => {
    try {
      const userName = localStorage.getItem('user_email');
      const keyContentsJson = {};
      (values.key_contents_json_array || []).forEach((pair) => {
        if (pair?.key?.trim()) {
          keyContentsJson[pair.key.trim()] = pair.value ?? '';
        }
      });
      const payload = {
        key_name: values.key_name,
        project_no: values.project_no,
        project_uuid: values.project_uuid,
        remarks: values.remarks,
        expiry_date: values.expiry_date ? values.expiry_date.toISOString() : null,
        status: 1,
        source: source,
        acct_email: userName,
        key_contents_json: keyContentsJson,
      };
      const data = await createConfigKeyApi(payload);
      if (data.success) {
        toastAlert.autoClose(true).showSuccess("新增成功");
        resetForm();
        handleClose();
        if (submitEffect) submitEffect();
      } else {
        // 判斷重複 key
        if (data.msg && typeof data.msg === "string" && data.msg.toLowerCase().includes("duplicate key value violates unique constraint")) {
          toastAlert.autoClose(true).showError("無法重複建立 Config Key");
        } else {
          toastAlert.autoClose(true).showError(data.msg || "新增失敗");
        }
        // AWS 帳戶重複或 STS 解析失敗等錯誤要讓使用者原地修正，不清空已填內容
      }
    } catch (err) {
      if (err.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    }
  };

  const handleParseGcpJson = (setFieldValue) => {
    try {
      const parsed = JSON.parse(gcpJsonText);
      setFieldValue('key_contents_json_array', mapGcpJsonToArray(parsed));
      toastAlert.autoClose(true).showSuccess("GCP JSON 解析成功");
    } catch (error) {
      toastAlert.autoClose(true).showError("GCP JSON 格式錯誤，請確認內容");
    }
  };

  return (
    <>
      <Modal
        animation="true"
        style={{ zIndex: '1299' }}
        show={isModalOpen}
        onHide={handleClose}
        className="ProjectManagement"
      >
        <Modal.Header>
          <Modal.Title className="modal-title">新增 Config Key</Modal.Title>
          {/* 解決籃框問題，自訂MUI按鈕 */}
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              marginLeft: 'auto'
            }}
          >
            <CloseIcon fontSize="medium" />
          </button>
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={formSchema}
          enableReinitialize={true}
        >
          {({ setFieldValue, isSubmitting, errors, touched, values }) => {
            const normalizedKeyName = normalizeKeyName(values.key_name);
            const fixedFields = getConfigKeyFields(normalizedKeyName);
            const fieldSpecByKey = Object.fromEntries(
              (fixedFields || []).map((field) => [field.key, field])
            );
            const isGcpKey = normalizedKeyName === GCP_KEY_NAME;
            const isAwsKey = normalizedKeyName === AWS_KEY_NAME;

            return (
            <Form>
              <ConfigKeyFieldsSync />
              <Modal.Body className="modal-body" style={{ paddingTop: '0px'}}>
                <Row>
                  <Col md="12">
                    <InputFormFiled
                      title="Key Name"
                      className="input-field custom-select"
                      name="key_name"
                      type="text"
                      invalid={errors.key_name && touched.key_name}
                    />
                  </Col>
                  <Col md="12" style={{ marginTop: '10px' }}>
                    <FieldArray name="key_contents_json_array">
                      {({ push, remove }) => (
                        <>
                          {fixedFields ? (
                            <div>
                              {/* 固定欄位由 key_name 決定，不提供新增／刪除列（與 Edit Modal 一致） */}
                              <p className="form-title mb-2" style={{ fontWeight: 'bold' }}>
                                {isAwsKey && 'AWS Secrets Manager 欄位'}
                                {isGcpKey && 'GCP Service Account 欄位'}
                                {!isAwsKey && !isGcpKey && `${normalizedKeyName} 欄位`}
                              </p>

                              {isGcpKey && (
                                <p className="form-title mb-2">
                                  同一專案可綁定多把 Service Account Key，每個 GCP 專案（PROJECT_ID）限一把。
                                  {existingGcpProjectIds.length > 0 && `　已綁定：${existingGcpProjectIds.join('、')}`}
                                </p>
                              )}

                              {isAwsKey && (
                                <p className="form-title mb-2">
                                  同一專案可綁定多把 Account Key，每個 AWS 帳戶限一把。
                                  帳戶 ID 會在儲存時由 AWS 自動識別。
                                  {existingAwsAccountIds.length > 0 && `　已綁定：${existingAwsAccountIds.join('、')}`}
                                </p>
                              )}

                              {isGcpKey && (
                                <>
                                  <Row className="mb-2">
                                    <Col md={12}>
                                      <Input
                                        type="textarea"
                                        rows={6}
                                        value={gcpJsonText}
                                        onChange={(e) => setGcpJsonText(e.target.value)}
                                        placeholder='可直接貼上整包 GCP service account JSON'
                                      />
                                    </Col>
                                  </Row>
                                  <Button
                                    type="button"
                                    variant="text"
                                    style={{ ...KEY_CONTENTS_ROW_BTN_STYLE, marginBottom: '10px', marginRight: '10px' }}
                                    onClick={() => handleParseGcpJson(setFieldValue)}
                                  >
                                    解析 JSON
                                  </Button>
                                </>
                              )}

                              {(values.key_contents_json_array || []).map((pair, index) => (
                                <Row key={`fixed-${pair.key}-${index}`} className="mb-2">
                                  <Col md={4}>
                                    <Input type="text" value={pair.key} disabled />
                                  </Col>
                                  <Col md={8}>
                                    {fieldSpecByKey[pair.key]?.multiline ? (
                                      <Input
                                        type="textarea"
                                        rows={5}
                                        name={`key_contents_json_array[${index}].value`}
                                        value={pair.value}
                                        onChange={(e) => setFieldValue(`key_contents_json_array[${index}].value`, e.target.value)}
                                        placeholder={fieldSpecByKey[pair.key]?.placeholder || ''}
                                      />
                                    ) : (
                                      <Input
                                        type="text"
                                        name={`key_contents_json_array[${index}].value`}
                                        value={pair.value}
                                        onChange={(e) => setFieldValue(`key_contents_json_array[${index}].value`, e.target.value)}
                                        placeholder={fieldSpecByKey[pair.key]?.placeholder || ''}
                                      />
                                    )}
                                  </Col>
                                </Row>
                              ))}
                            </div>
                          ) : (
                            <div>
                              <p className="form-title mb-2" style={{ fontWeight: 'bold' }}>Key Contents</p>
                              {(values.key_contents_json_array || []).map((pair, index) => (
                                <Row key={`kv-${index}`} className="mb-2">
                                  <Col md={5}>
                                    <Input
                                      type="text"
                                      name={`key_contents_json_array[${index}].key`}
                                      value={pair.key}
                                      onChange={(e) => setFieldValue(`key_contents_json_array[${index}].key`, e.target.value)}
                                      placeholder="Key"
                                    />
                                  </Col>
                                  <Col md={5}>
                                    <Input
                                      type="text"
                                      name={`key_contents_json_array[${index}].value`}
                                      value={pair.value}
                                      onChange={(e) => setFieldValue(`key_contents_json_array[${index}].value`, e.target.value)}
                                      placeholder="Value"
                                    />
                                  </Col>
                                  <Col md={2}>
                                    <Button
                                      type="button"
                                      className="btn btn-cancel"
                                      onClick={() => remove(index)}
                                      disabled={(values.key_contents_json_array || []).length === 1}
                                    >
                                      刪除
                                    </Button>
                                  </Col>
                                </Row>
                              ))}
                              <Button
                                type="button"
                                className="btn btn-cancel"
                                onClick={() => push({ key: '', value: '' })}
                              >
                                新增欄位
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </Col>
                  {/* <Col md="12">
                    <InputFormFiled
                      title="Project No"
                      className="input-field custom-select"
                      name="project_no"
                      disabled
                      type="text"
                      invalid={errors.project_no && touched.project_no}
                    />
                  </Col> */}
                  <Col md="12">
                    <InputFormFiled
                      title="備註"
                      className="input-field custom-select"
                      name="remarks"
                      type="text"
                      invalid={errors.remarks && touched.remarks}
                    />
                  </Col>
                  <Col md={12}>
                    <p
                      className="form-title mb-0"
                      style={{
                        fontSize: '12px',
                        color: '#323232',
                        fontWeight: 'bold',
                        marginTop: '20px',
                        marginBottom: '6px',
                        fontFamily: 'Noto Sans, sans-serif',
                      }}
                      >
                      到期日
                    </p>
                    <DatePicker
                      selected={values.expiry_date}
                      onChange={(date) => setFieldValue("expiry_date", date)}
                      showTimeInput
                      timeInputLabel="Time:"
                      dateFormat="yyyy-MM-dd HH:mm:ss"
                      className="form-control"
                    />
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer className="modal-footer">
                <Button
                  type="button"
                  className="btn btn-cancel"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  取消
                </Button>
                <Button type="submit" className="btn btn-custom" disabled={isSubmitting}>
                  儲存
                </Button>
              </Modal.Footer>
            </Form>
            );
          }}
      </Formik>
      </Modal>
    </>
  );
};

export default CloudConfigKeyAddModal;
